<template>
  <v-container style="max-width:1000px" class="mt-8">
    <v-container>
      <h1>Create your very own metro map!</h1>
      <p class="">
        Follow the three guided steps and create a metro map of your own data! Start by uploading a file, that adheres to the input <router-link :to="{ name: 'help'}" >requirements</router-link>. Then you can add a title, a description and decide if other random people can view your map in our gallery. Last but not least you can use our suggested presets, or play around with different settings, to draw the metro map.
      </p>
      <p>
        If you want to test the tool and don't have a data set on hand, try one of the example data sets (<router-link :to="{ name: 'dataDetail', params: { dataId: '88e4bb02-88dd-4648-89ba-c3284e9f346f' }}">Simpsons</router-link>, <router-link :to="{ name: 'dataDetail', params: { dataId: '93749e07-dbf8-4643-a1fb-1e8f4c96b71a' }}">Magic: The Gathering</router-link>, <router-link :to="{ name: 'dataDetail', params: { dataId: '452ddcc3-abb2-4cf6-b7fd-fb7d8617b509' }}">Rock'n'Roll</router-link>)
      </p>
    </v-container>
    <v-stepper v-model="stage" vertical>
      <v-stepper-step :complete="stage > 1" step="1">
        Select your Data
      </v-stepper-step>

      <v-stepper-content step="1">
        <v-container fluid>
          <v-row align="stretch">
            <v-col cols="12" md="6">
              <v-card height="100%">
                <v-card-title>
                  Select File...
                </v-card-title>
                <v-card-text>
                  <v-file-input @change="submitFile" accept=".csv" label="File input"></v-file-input>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card>
                <v-card-title>
                  ...or Paste JSON
                </v-card-title>
                <v-card-text>
                  <v-textarea
                    label="Enter JSON Data"
                    rows="8"
                    row-height="30"
                    shaped
                    @change="submitData"
                  ></v-textarea>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-show='errorFile' class=".subtitle-1">
          <span class="red--text">{{errorFileMessage}}</span>
        </v-container>
        <div class="d-flex pa-2">
          <v-btn color="secondary textSecondary--text" :disabled='stage1NotComplete' @click="changeToStage2">Continue</v-btn>
          <v-btn text class="mx-4" :to="{ name: 'home'}">Cancel</v-btn>
          <v-spacer/>
          <v-btn class="mx-2" icon small color="secondary" :to="{ name: 'help'}">
            <v-icon dark>mdi-help</v-icon>
          </v-btn>
        </div>
      </v-stepper-content>

      <v-stepper-step :complete="stage > 2" step="2">
        Upload Data
      </v-stepper-step>

      <v-stepper-content step="2">
          <v-container fluid>
            <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>Show Data Table</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <DataTable :headers="headers" :items="items" text="Show Data Table"/>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            <v-text-field
              v-model="payload.name"
              class="mt-12"
              :rules="[v => v.length <= 64 || 'Max 64 characters']"
              counter="64"
              hint="Enter a meaningful title"
              label="Title of Data"
            />

            <v-textarea
              v-model="payload.description"
              label="Data Description"
              hint="Enter a meaningful data description"
              rows="3"
              shaped
              :rules="[v => v.length <= 256 || 'Max 256 characters']"
              counter="256"
            ></v-textarea>

            <v-checkbox
              v-model="payload.isHidden"
              label="Hide your data from other users"
            ></v-checkbox>
            <v-container v-show='payload.isHidden' class=".subtitle-1">
              <span class="red--text">Your Data will be unlisted. Although it is quite hard, it could still be found by brute force!</span>
            </v-container>
            <v-card flat v-show='canSort'>
              <v-card-title>
                Enter partial Order
              </v-card-title>
              <v-card-text>
                <div v-for="sortO in sortObject" :key="sortO">
                  <v-textarea
                    v-model="sortO.value"
                    :label="sortO.key"
                    hint="Enter a partial order for the attribute"
                    rows="1"
                    shaped
                  ></v-textarea>
                </div>
              </v-card-text>
            </v-card>
          </v-container>
        <v-container v-show='errorUpload' class=".subtitle-1">
          <span class="red--text">{{errorUploadMessage}}</span>
        </v-container>
        <div class="d-flex pa-2">
          <v-btn color="secondary textSecondary--text" @click="submit" :loading="stage2Loading" >Upload</v-btn>
          <v-btn text class="mx-4" :to="{ name: 'home'}">Cancel</v-btn>
          <v-spacer/>
          <v-btn class="mx-2" icon small color="secondary" :to="{ name: 'help'}">
            <v-icon dark>mdi-help</v-icon>
          </v-btn>
        </div>
      </v-stepper-content>

      <v-stepper-step :complete="stage > 3" step="3">
        Create Map
      </v-stepper-step>

      <v-stepper-content step="3">
    <v-expansion-panels v-model="panel">
        <v-expansion-panel>
            <v-expansion-panel-header>Show Data Table</v-expansion-panel-header>
            <v-expansion-panel-content>
              <DataTable :headers="headers" :items="items" text="Show Data Table"/>
            </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
            <v-expansion-panel-header>Presets</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-select
                v-model="presetChoice"
                :items="preset_choices"
                item-text="text"
                item-value="value"
                hint="Select a pipeline preset"
                persistent-hint
                single-line
                v-on:change="changePreset"
                ></v-select>
            </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
            <v-expansion-panel-header>Expert Mode</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-select
                v-model="payloadMap.support_algorithm"
                :items="support_choices"
                item-text="text"
                item-value="value"
                hint="Select a support algorithm"
                persistent-hint
                single-line
                ></v-select>
              <v-select
                v-model="payloadMap.insertion_algorithm"
                :items="insertion_choices"
                item-text="text"
                item-value="value"
                hint="Select an insertion algorithm"
                persistent-hint
                single-line
                ></v-select>
              <v-select
                v-model="payloadMap.initial_layout_algorithm"
                :items="initial_layout_choices"
                item-text="text"
                item-value="value"
                hint="Select a layout algorithm"
                persistent-hint
                single-line
                ></v-select>
              <v-select
                v-model="payloadMap.schematization_algorithm"
                :items="schematization_choices"
                item-text="text"
                item-value="value"
                hint="Select a schematization algorithm"
                persistent-hint
                single-line
                ></v-select>

                <div v-if="canSort">
                  <v-checkbox
                    v-model="payloadMap.hasSorting"
                    label="Apply defined partial order"
                  />
                  <v-select
                  v-model="payloadMap.sortColumn"
                  :items="sortObject"
                  item-text="key"
                  item-value="column"
                  :disabled="!payloadMap.hasSorting"
                  hint="Select an attribute for ordering along a metro line"
                  persistent-hint
                  single-line
                  ></v-select>
                </div>
            </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>

        <div class="d-flex px-2 mt-8">
          <v-btn color="secondary textSecondary--text" @click="createMap">Create</v-btn>
          <v-btn text class="mx-4" :to="{ name: 'home'}">Cancel</v-btn>
          <v-spacer/>
          <v-btn class="mx-2" icon small color="secondary" :to="{ name: 'help'}">
            <v-icon dark>mdi-help</v-icon>
          </v-btn>
        </div>

      </v-stepper-content>

    </v-stepper>
  </v-container>
</template>

<script>

import DataTable from '../components/DataTable.vue'

export default {
  name: 'Create',
  components: {
    DataTable
  },
  data () {
    return {
      stage: 1,
      data: '',
      payload: {
        raw: '',
        name: '',
        description: '',
        setColumns: '',
        dataColumns: '',
        sortOrder: '',
        isHidden: false
      },
      payloadMap: {
        data: '',
        support_algorithm: '',
        initial_layout_algorithm: '',
        insertion_algorithm: '',
        schematization_algorithm: '',
        hasSorting: false,
        sortColumn: 0
      },
      sortObject: [],
      stage1NotComplete: true,
      stage2Loading: false,
      stage3Loading: false,
      errorFile: false,
      errorFileMessage: '',
      errorUpload: false,
      errorUploadMessage: '',
      headers: [],
      items: [],
      formData: '',
      support_choices: '',
      initial_layout_choices: '',
      insertion_choices: '',
      schematization_choices: '',
      canSort: false,
      sort_choices: '',
      presetChoice: '',
      preset_choices: '',
      panel: 1
    }
  },
  methods: {
    submitData (value) {
      this.stage1NotComplete = true
      var results = this.$papa.parse(value, {})

      if (this.checkDataClientSide(results)) {
        this.data = results
        this.errorPaste = false
        this.stage1NotComplete = false
      } else {
        this.errorPaste = true
      }
    },

    submitFile (value) {
      this.stage1NotComplete = true
      var tthis = this
      this.$papa.parse(value, {
        keepEmptyRows: false,
        skipEmptyLines: true,
        complete: function (results, file) {
          if (tthis.checkDataClientSide(results)) {
            tthis.errorFile = false
            tthis.data = results
            tthis.stage1NotComplete = false
          } else {
            tthis.errorFile = true
          }
        },
        error: function (error, file, inputElem, reason) {
          if (error) {
            this.errorFileMessage = 'Error parsing your file: ' + reason
            this.errorFile = true
          }
        }
      })
    },

    changeToStage2 () {
      this.payload.raw = this.data.data
      this.payload.setColumns = this.data.setColumns
      this.payload.dataColumns = this.data.dataColumns

      this.headers = [{ value: this.data.data[0][0], text: this.data.data[0][0] }]
      this.data.setColumns.forEach(d => {
        this.headers.push({ value: this.data.data[0][d] })
      })

      this.data.dataColumns.forEach(d => {
        this.sortObject.push({ key: this.data.data[0][d], value: '', column: d })
        this.headers.push({ value: this.data.data[0][d], text: this.data.data[0][d] })
      })

      if (this.sortObject.length > 0) {
        this.canSort = true
      }

      this.items = []
      this.data.data.forEach((row, ind) => {
        if (ind < 1) {
          return
        }

        var entry = {}
        row.forEach((elem, i) => {
          entry[this.data.data[0][i]] = elem
        })
        this.items.push(entry)
      })

      this.stage = 2
    },

    submit () {
      this.stage2Loading = true

      // this.payload.raw = this.data.data
      // this.payload.dataColumns = this.data.dataColumns
      // this.payload.setColumns = this.data.setColumns
      this.payload.sortOrder = {}

      this.sortObject.forEach(d => {
        this.payload.sortOrder[d.key] = d.value
      })

      this.axios
        .post('data/',
          this.payload
        )
        .then(response => {
          this.loadForms()

          this.payloadMap.data = response.data.id
          this.stage = 3
          this.stage2Loading = false
          this.errorUpload = false
        }, (error) => {
          this.stage2Loading = false

          if (error.response) {
            this.errorUploadMessage = ''
            for (const [key, value] of Object.entries(error.response.data.error)) {
              this.errorUploadMessage += key + ': ' + value
            }
          } else {
            this.errorUploadMessage = 'Error Uploading to Server'
          }
          this.errorUpload = true
        })
    },

    createMap: function () {
      this.stage3Loading = true
      this.axios
        .post('maps/',
          this.payloadMap
        )
        .then(response => {
          this.stage3Loading = false
          this.$router.push({ name: 'mapDetail', params: { mapId: response.data } })
        }, (error) => {
          this.stage3Loading = false
          this.errorMessage = error
          this.hasError = true
        })
    },

    loadForms: function () {
      this.axios.get('form_choices/')
        .then(response => {
          this.formData = response.data

          this.support_choices = []
          this.initial_layout_choices = []
          this.insertion_choices = []
          this.schematization_choices = []
          this.preset_choices = []

          this.formData['support_choices'].forEach(item => this.support_choices.push({ value: item[0], text: item[1] }))
          this.formData['initial_layout_choices'].forEach(item => this.initial_layout_choices.push({ value: item[0], text: item[1] }))
          this.formData['insertion_choices'].forEach(item => this.insertion_choices.push({ value: item[0], text: item[1] }))
          this.formData['schematization_choices'].forEach(item => this.schematization_choices.push({ value: item[0], text: item[1] }))

          for (const [key, value] of Object.entries(this.formData['presets'])) {
            this.preset_choices.push({ value: value, text: key })
          }

          this.presetChoice = this.preset_choices[0]['value']
          this.payloadMap.support_algorithm = this.support_choices[this.presetChoice[0]]['value']
          this.payloadMap.initial_layout_algorithm = this.initial_layout_choices[this.presetChoice[2]]['value']
          this.payloadMap.insertion_algorithm = this.insertion_choices[this.presetChoice[1]]['value']
          this.payloadMap.schematization_algorithm = this.schematization_choices[this.presetChoice[3]]['value']
        })
    },

    changePreset: function () {
      this.payloadMap.support_algorithm = this.support_choices[this.presetChoice[0]]['value']
      this.payloadMap.initial_layout_algorithm = this.initial_layout_choices[this.presetChoice[2]]['value']
      this.payloadMap.insertion_algorithm = this.insertion_choices[this.presetChoice[1]]['value']
      this.payloadMap.schematization_algorithm = this.schematization_choices[this.presetChoice[3]]['value']
    },

    checkDataClientSide (d) {
      var dataColumns = []
      var setColumns = []

      var data = d.data
      var header = data[0]

      if (data.length < 3) {
        this.errorFileMessage = 'Dataset must have at leas 2 rows with data'
        return false
      }

      for (var i = 1; i < header.length; i++) {
        var string = header[i]
        var firstC = string.charAt(0)
        var lastC = string.charAt(string.length - 1)

        if (firstC === '{' && lastC === '}') {
          dataColumns.push(i)
        } else {
          setColumns.push(i)
        }
      }

      if (setColumns.length < 1) {
        this.errorFileMessage = 'No set definition found'
        return false
      }

      for (i = 1; i < data.length; i++) {
        if (data[i].length < data[0].length) {
          this.errorFileMessage = 'Error parsing data: Row ' + i + ' has less elements than the header specifies'
          return false
        }

        for (var j = 0; j < setColumns.length; j++) {
          var index = setColumns[j]
          var dataPoint = data[i][index]

          if (dataPoint === '0') {
            d.data[i][index] = false
          } else if (dataPoint === '1') {
            d.data[i][index] = true
          } else {
            this.errorFileMessage = 'Error parsing data: Row ' + i + ' Column ' + j + '. Is the value 0 or 1?'
            return false
          }
        }
      }

      d['dataColumns'] = dataColumns
      d['setColumns'] = setColumns

      return true
    }
  }
}
</script>

<style>
    .card-custom{
        padding-bottom:50px
    }

    .button{
        width:200px;
    }

    .card-footer{
    position:absolute;
    bottom:0;
    left:0;
    width:100%;
    }
</style>
